import { Component } from '@theme/component';

/**
 * Carousel Navigation Bar Component
 * Progress bar style navigation that syncs with slideshow component
 * Features: Left/Right arrow navigation buttons
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} carouselProgress
 * @property {HTMLElement} navbarTrack
 * @property {HTMLButtonElement} prevButton
 * @property {HTMLButtonElement} nextButton
 *
 * @extends {Component<Refs>}
 */
class CarouselNavbar extends Component {
  requiredRefs = ['carouselProgress', 'navbarTrack', 'prevButton', 'nextButton'];

  connectedCallback() {
    try {
      super.connectedCallback();

      // Get the slide count from data attribute
      this.slideCount = parseInt(this.getAttribute('data-slide-count'), 10) || 1;

      // Initialize progress bar
      if (this.refs.carouselProgress) {
        this.updateProgress(0);
      } else {
        console.error('CarouselNavbar: carouselProgress ref not found!');
      }

      // Set up arrow buttons
      this.setupArrowButtons();

      // Defer finding slideshow to allow DOM to settle
      setTimeout(() => {
        this.findSlideshowComponent();

        // Set up scroll observer if slideshow was found
        if (this.slideshowComponent) {
          this.setupScrollObserver();
          this.setupTrackInteraction();
          this.updateArrowStates();
        } else {
          console.warn('CarouselNavbar: No slideshow found');
        }
      }, 500);
    } catch (error) {
      console.error('CarouselNavbar: Error in connectedCallback', error);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cancel any pending animation frames
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    // Remove scroll listeners
    if (this.scroller && this.boundUpdateProgress) {
      this.scroller.removeEventListener('scroll', this.boundUpdateProgress);
      this.scroller.removeEventListener('touchmove', this.boundUpdateProgress);
      this.scroller.removeEventListener('mousemove', this.boundUpdateProgress);
    }
  }

  /**
   * Find the associated slideshow component
   */
  findSlideshowComponent() {
    // Look for slideshow-component in the same section
    const section = this.closest('.section, .section-resource-list');
    if (!section) {
      console.warn('CarouselNavbar: Could not find parent section');
      return;
    }

    // Try to find slideshow component in resource-list__carousel
    const carouselContainer = section.querySelector('.resource-list__carousel');
    if (carouselContainer) {
      this.slideshowComponent = carouselContainer.querySelector('slideshow-component');
    }

    // Fallback to any slideshow in section
    if (!this.slideshowComponent) {
      this.slideshowComponent = section.querySelector('slideshow-component');
    }

    if (!this.slideshowComponent) {
      console.warn('CarouselNavbar: Could not find slideshow-component');
      return;
    }

    // Listen for slideshow select events
    this.slideshowComponent.addEventListener('slideshow:select', (event) => {
      if (event.detail?.index !== undefined) {
        this.updateProgress(event.detail.index);
      }
    });
  }

  /**
   * Set up scroll observer to track carousel progress
   */
  setupScrollObserver() {
    const scroller = this.slideshowComponent?.querySelector('slideshow-slides');

    if (!scroller) {
      console.warn('CarouselNavbar: Could not find slideshow-slides');
      return;
    }

    // Store scroller reference
    this.scroller = scroller;

    // Create bound update function for removing listener later
    this.boundUpdateProgress = () => {
      this.updateProgressFromScroll(scroller);
      this.updateArrowStates();
    };

    // Update progress on scroll (works for touch drag, mouse drag, and programmatic scroll)
    scroller.addEventListener('scroll', this.boundUpdateProgress, { passive: true });

    // Also listen for touch events to ensure immediate feedback
    scroller.addEventListener('touchmove', this.boundUpdateProgress, { passive: true });

    // Listen for mouse drag
    scroller.addEventListener('mousemove', this.boundUpdateProgress, { passive: true });

    // Initial update
    this.updateProgressFromScroll(scroller);
  }

  /**
   * Set up click/touch interaction on the track for seeking
   */
  setupTrackInteraction() {
    const track = this.refs.navbarTrack;

    if (!track) {
      console.warn('CarouselNavbar: navbarTrack ref not found');
      return;
    }

    let isDragging = false;
    let dragStartX = 0;
    let hasMoved = false;

    // Handle mouse down
    track.addEventListener('mousedown', (e) => {
      isDragging = true;
      hasMoved = false;
      dragStartX = e.clientX;
      this.handleTrackClick(e);
    });

    // Handle mouse move (drag)
    document.addEventListener('mousemove', (e) => {
      if (isDragging) {
        // Check if mouse moved significantly
        if (Math.abs(e.clientX - dragStartX) > 3) {
          hasMoved = true;
        }
        this.handleTrackClick(e);
      }
    });

    // Handle mouse up
    document.addEventListener('mouseup', (e) => {
      if (isDragging && !hasMoved) {
        // It was a click, not a drag
        this.handleTrackClick(e);
      }
      isDragging = false;
      hasMoved = false;
    });

    // Handle touch start
    track.addEventListener('touchstart', (e) => {
      hasMoved = false;
      dragStartX = e.touches[0].clientX;
      this.handleTrackClick(e.touches[0]);
    }, { passive: true });

    // Handle touch move (drag)
    track.addEventListener('touchmove', (e) => {
      if (Math.abs(e.touches[0].clientX - dragStartX) > 3) {
        hasMoved = true;
      }
      this.handleTrackClick(e.touches[0]);
    }, { passive: true });

    // Handle touch end
    track.addEventListener('touchend', (e) => {
      hasMoved = false;
    }, { passive: true });
  }

  /**
   * Handle click/touch on track to seek to position
   * @param {MouseEvent|Touch} event - The click or touch event
   */
  handleTrackClick(event) {
    const track = this.refs.navbarTrack;
    if (!track || !this.scroller) return;

    const rect = track.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));

    // Calculate scroll position
    const scrollWidth = this.scroller.scrollWidth - this.scroller.clientWidth;
    const scrollTo = percentage * scrollWidth;

    // Scroll to position
    this.scroller.scrollTo({
      left: scrollTo,
      behavior: 'smooth'
    });
  }

  /**
   * Update progress bar based on scroll position
   * @param {HTMLElement} scroller - The slideshow scroller element
   */
  updateProgressFromScroll(scroller) {
    const scrollLeft = scroller.scrollLeft;
    const scrollWidth = scroller.scrollWidth - scroller.clientWidth;

    if (scrollWidth <= 0) {
      this.setProgressWidth(0);
      return;
    }

    // Calculate progress as a percentage (0 to 1)
    const scrollProgress = Math.max(0, Math.min(1, scrollLeft / scrollWidth));

    // Update progress bar directly with scroll percentage
    this.setProgressWidth(scrollProgress * 100);
  }

  /**
   * Set progress bar width (optimized to avoid layout thrashing)
   * @param {number} percent - Progress percentage (0-100)
   */
  setProgressWidth(percent) {
    if (!this.refs.carouselProgress) return;

    // Use requestAnimationFrame for smooth updates
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.refs.carouselProgress.style.width = `${percent}%`;
    });
  }

  /**
   * Update the progress bar width based on current slide
   * @param {number} currentIndex - The current slide index (0-based)
   */
  updateProgress(currentIndex) {
    if (!this.refs.carouselProgress) return;

    // Calculate progress percentage based on completed slides
    // Progress fills completely when reaching the last slide
    const progressPercent = this.slideCount > 1
      ? (currentIndex / (this.slideCount - 1)) * 100
      : 0;

    // Update the progress bar width using optimized method
    this.setProgressWidth(progressPercent);

    // Store current index
    this.currentIndex = currentIndex;
  }

  /**
   * Get the current progress (0 to 1)
   */
  get progress() {
    return this.slideCount > 1 ? this.currentIndex / (this.slideCount - 1) : 0;
  }

  /**
   * Set up arrow button click handlers
   */
  setupArrowButtons() {
    const prevButton = this.refs.prevButton;
    const nextButton = this.refs.nextButton;

    if (!prevButton || !nextButton) {
      console.warn('CarouselNavbar: Arrow buttons not found');
      return;
    }

    // Previous button click handler
    prevButton.addEventListener('click', () => {
      this.navigatePrev();
    });

    // Next button click handler
    nextButton.addEventListener('click', () => {
      this.navigateNext();
    });
  }

  /**
   * Navigate to previous slide
   */
  navigatePrev() {
    if (!this.scroller) return;

    const scrollLeft = this.scroller.scrollLeft;
    const slideWidth = this.scroller.clientWidth;

    // Scroll backwards by approximately 1/3 of the visible width (one card)
    const scrollDistance = slideWidth * 0.33;

    // Scroll backwards
    this.scroller.scrollTo({
      left: Math.max(0, scrollLeft - scrollDistance),
      behavior: 'smooth'
    });

    // Update arrow states after scroll
    setTimeout(() => this.updateArrowStates(), 350);
  }

  /**
   * Navigate to next slide
   */
  navigateNext() {
    if (!this.scroller) return;

    const scrollLeft = this.scroller.scrollLeft;
    const slideWidth = this.scroller.clientWidth;
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

    // Scroll forwards by approximately 1/3 of the visible width (one card)
    const scrollDistance = slideWidth * 0.33;

    // Scroll forwards
    this.scroller.scrollTo({
      left: Math.min(maxScroll, scrollLeft + scrollDistance),
      behavior: 'smooth'
    });

    // Update arrow states after scroll
    setTimeout(() => this.updateArrowStates(), 350);
  }

  /**
   * Update arrow button disabled states based on scroll position
   */
  updateArrowStates() {
    if (!this.scroller || !this.refs.prevButton || !this.refs.nextButton) return;

    const scrollLeft = this.scroller.scrollLeft;
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

    // Disable prev button if at start
    if (scrollLeft <= 5) {
      this.refs.prevButton.disabled = true;
    } else {
      this.refs.prevButton.disabled = false;
    }

    // Disable next button if at end
    if (scrollLeft >= maxScroll - 5) {
      this.refs.nextButton.disabled = true;
    } else {
      this.refs.nextButton.disabled = false;
    }
  }
}

customElements.define('carousel-navbar', CarouselNavbar);

console.log('carousel-navbar component registered successfully');
